import AMapLoader from '@amap/amap-jsapi-loader';
import { store as starkStore } from '@ice/stark-data';

declare global {
  interface Window {
    _AMapSecurityConfig?: {
      securityJsCode: string;
    };
  }
}

export type AMapReactConfig = {
  version: string;
  key: string;
  securityJsCode: string;
  plugins: string[];
};

const DEFAULT_CONFIG: AMapReactConfig = {
  version: '2.0',
  key: 'e9fe32149387d6ae7072892c56324b2a',
  securityJsCode: 'ec2db69d245329d3781e64baafb0ed59',
  // 固定为 superset，确保子应用不会因 plugins 不一致触发 loader 的“混用”保护。
  plugins: [
    'AMap.Scale',
    'AMap.ToolBar',
    'AMap.Marker',
    'AMap.Icon',
    'AMap.CircleMarker',
    'AMap.Polyline',
    'AMap.GeometryUtil',
    'AMap.Driving',
    'AMap.Geocoder',
    'AMap.AutoComplete',
    'AMap.MarkerCluster',
  ],
};

let cachedLoadPromise: Promise<any> | null = null;
let cachedAMap: any | null = null;

function setSecurityConfig(securityJsCode: string) {
  if (typeof window === 'undefined') return;

  if (window._AMapSecurityConfig) {
    window._AMapSecurityConfig.securityJsCode = securityJsCode;
  } else {
    window._AMapSecurityConfig = { securityJsCode };
  }
}

/**
 * 主应用统一加载 AMap JSAPI（window 级单例），并通过 icestark store 对外暴露。
 */
export function ensureAMapSdkLoaded(config: Partial<AMapReactConfig> = {}) {
  const finalConfig: AMapReactConfig = {
    ...DEFAULT_CONFIG,
    ...config,
    plugins: config.plugins ?? DEFAULT_CONFIG.plugins,
  };

  console.log('[AMAP][host] ensureAMapSdkLoaded called', {
    version: finalConfig.version,
    key: finalConfig.key,
    securityJsCode: finalConfig.securityJsCode,
    plugins: finalConfig.plugins,
  });

  if (cachedAMap) {
    console.log('[AMAP][host] already loaded, reuse cached instance');
    return Promise.resolve(cachedAMap);
  }

  if (cachedLoadPromise) {
    console.log('[AMAP][host] load in progress, reuse cached promise');
    return cachedLoadPromise;
  }

  console.log('[AMAP][host] setting window._AMapSecurityConfig');
  setSecurityConfig(finalConfig.securityJsCode);

  console.log('[AMAP][host] start AMapLoader.load');

  cachedLoadPromise = AMapLoader.load({
    key: finalConfig.key,
    version: finalConfig.version,
    plugins: finalConfig.plugins,
  })
    .then((AMap) => {
      console.log('[AMAP][host] AMapLoader.load success', {
        hasAMap: !!AMap,
        windowHasAMap: typeof window !== 'undefined' ? !!(window as any).AMap : undefined,
      });
      cachedAMap = AMap;
      starkStore.set('amap:config', finalConfig);
      starkStore.set('amap:loaded', true);
      starkStore.set('amap:instance', AMap);
      return AMap;
    })
    .catch((err) => {
      console.error('[AMAP][host] AMapLoader.load failed', err);
      cachedLoadPromise = null;
      starkStore.set('amap:loaded', false);
      starkStore.set('amap:error', err);
      throw err;
    });

  return cachedLoadPromise;
}
